import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AmorgAdminToken, Article, ArticleContact, ArticleList, ArticleSubscriber, ArticleType, rng } from '../../../amorg-common';
import { getActiveUser } from '../../../api/adminClientApi';
import { articlesClientApi } from '../../../api/articlesClientApi';
import { ImportanceAvatar } from '../../../designSystem/avatar/ImportanceAvatar';
import { Button } from '../../../designSystem/button/Button';
import { LinkButton } from '../../../designSystem/button/LinkButton';
import { colors } from '../../../designSystem/colors/color';
import { ConfirmationButton, ConfirmationModal } from '../../../designSystem/compositeComponents/ConfirmationButton';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { OnOff } from '../../../designSystem/inputs/OnOff';
import { Label } from '../../../designSystem/Label';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { toDateTimeString } from '../../../utils/date';
import { ArticlesTab } from './_components/ArticlesTab';
import { ArticleViewAppendixPanel } from './_components/articleView/ArticleViewAppendixPanel';
import { ArticleViewFields } from './_components/articleView/ArticleViewFields';
import { ArticleViewNotesPanel } from './_components/articleView/ArticleViewNotesPanel';
import { ArticleViewReleasePanel } from './_components/articleView/ArticleViewReleasePanel';
import { ArticleViewSubscriberTable } from './_components/articleView/ArticleViewSubscriberPanel';
import { AmorgyPageWrapper } from '../../_pageWrappers/AmorgyPageWrapper';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';
import { MainRightTwoColumnLayout } from '../../_layouts/TwoColumnLayouts';
import { Card } from '../../../designSystem/Card';
import { InfoChip } from '../../../designSystem/Chip';
import { SelectField } from '../../../designSystem/formField/SelectField';
import { Em } from '../../../designSystem/typography/Em';
import { MessageCard } from '../../../designSystem/MessageCard';

export default function ArticlesView (): JSX.Element 
{
    const params = useParams<{ id: string; }>();

    const [activeUser, setActiveUser] = React.useState<AmorgAdminToken|null>(null);
    const [lists, setLists] = React.useState<ArticleList[]>([]);
    const [types, setTypes] = React.useState<ArticleType[]>([]);
    const [initialLoad, setInitialLoad] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [deletionError, setDeletionError] = React.useState<string | null>(null);

    const [articleContacts, setArticleContacts] = React.useState<ArticleContact[]>([]);
    const [articles, setArticles] = React.useState<Article[]>([]);
    const [articleIndex, setArticleIndex] = React.useState(0);
    const [subscribers, setSubscribers] = React.useState<ArticleSubscriber[]>([]);

    const [selectedView, setSelectedView] = React.useState<'article' | 'subscribers' | 'notes' | 'releases'>('article');

    const navigate = useNavigate();
    const latestArticle = articles[0];

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            setLists(await articlesClientApi.getLists());
            setTypes(await articlesClientApi.getTypes());
            const arts = await articlesClientApi.getArticleVersions(params.id || '');
            setArticles(arts.versions);
            setSubscribers(arts.subscribers);
            const index = 0;
            setArticleIndex(index);
            const contacts = await articlesClientApi.getContacts(undefined, arts.versions[0].articleId);
            setArticleContacts(contacts);

            setActiveUser(await getActiveUser(() => navigate('/amorgy/login')));

            setInitialLoad(true);
            setLoading(false);
        };
        getData();
    }, []);

    async function handleClickCreateVersion ()
    {
        setLoading(true);

        await articlesClientApi.createArticleVersion(params.id || '', rng.getRandomId());
        const arts = await articlesClientApi.getArticleVersions(params.id || '');
        setArticles(arts.versions);
        const index = 0;
        setArticleIndex(index);

        setLoading(false);
    }

    async function handleClickReleaseVersion (silentRelease: boolean)
    {
        setLoading(true);

        await articlesClientApi.releaseArticleVersion(params.id || '', latestArticle.version, silentRelease);
        const arts = await articlesClientApi.getArticleVersions(params.id || '');
        setArticles(arts.versions);
        const index = 0;
        setArticleIndex(index);

        setLoading(false);
    }

    async function handleClickMergeUnreleasedVersion ()
    {
        setLoading(true);

        await articlesClientApi.mergeUnreleasedArticleVersion(params.id || '', latestArticle.version);
        const arts = await articlesClientApi.getArticleVersions(params.id || '');
        setArticles(arts.versions);
        const index = 0;
        setArticleIndex(index);

        setLoading(false);
    }

    async function handleReload ()
    {
        setLoading(true);
        const arts = await articlesClientApi.getArticleVersions(params.id || '');
        setArticles(arts.versions);
        setSubscribers(arts.subscribers);
        const index = 0;
        setArticleIndex(index);

        setLoading(false);
    }

    async function handleSetArticleDeleted (isDeleted: boolean)
    {
        setDeletionError(null);
        setLoading(true);
        try
        {
            const result = await articlesClientApi.setArticleDeleted(params.id || '', isDeleted);
            if (result.status !== 'success')
            {
                setDeletionError(result.status === 'failure' ? result.clientMessage : 'Der opstod en fejl');
                return;
            }

            const arts = await articlesClientApi.getArticleVersions(params.id || '');
            setArticles(arts.versions);
            setSubscribers(arts.subscribers);
            setArticleIndex(0);
        }
        finally
        {
            setSelectedView('article');
            setLoading(false);
        }
    }

    const article = articles ? articles[articleIndex] : null;

    return (
        <AmorgyPageWrapper selectedTab='articles' subMenuContent={
            <ArticlesTab selectedTab='articles' />
        }>
            <LoadingPageOverlay loading={loading} />
            <SingleColumnLayout>

                <Flex flexDirection='column' gap={16} marginBotttom={40}>

                    {initialLoad &&
                        <>
                            <Flex>
                                <LinkButton variant='text' text='Tilbage til oversigten' href='/amorgy/articles' />

                                <div style={{ flexGrow: 1, gap: '8px', justifyContent: 'flex-end', alignItems: 'center', display: 'flex' }}>

                                    <Button variant='filled' disabled={latestArticle.release === null || latestArticle.isDeleted} text='Opret ny version' onClick={handleClickCreateVersion} />

                                    <ReleaseModalButton
                                        articleAssessment={latestArticle.assessment}
                                        articleDescription={latestArticle.changeDescription}
                                        articleVersion={latestArticle.version}
                                        disabled={latestArticle.release !== null || latestArticle.isDeleted}
                                        onClickRelease={handleClickReleaseVersion}
                                    />

                                    <MergeModalButton
                                        articleVersion={latestArticle.version}
                                        disabled={latestArticle.isDeleted || latestArticle.release !== null || latestArticle.version < 2}
                                        onClickMerge={handleClickMergeUnreleasedVersion}
                                    />

                                    {article &&
                                        <ConfirmationButton
                                            buttonText={article.isDeleted ? 'Gendan lovkrav' : 'Slet lovkrav'}
                                            title={article.isDeleted ? 'Gendan lovkrav' : 'Slet lovkrav'}
                                            content={
                                                <>{`Er du sikker på at du vil ${article.isDeleted ? 'gendanne' : 'slette'} lovkravet ?${!article.isDeleted ? ' Evt. abonenter vil blive fjernet når lovkravet slettes, og vil ikke modtage en automatisk notifikation.' : ''}`}</>
                                            }
                                            icon='interface_actions_delete'
                                            modalButtonVariant='destructive'
                                            requireKey
                                            onConfirm={() => handleSetArticleDeleted(!article.isDeleted)}
                                        />
                                    }

                                </div>
                            </Flex>
                            {deletionError &&
                                <div style={{ color: colors.icon.error }}>{deletionError}</div>
                            }
                            <Flex justifyContent='flex-end' gap={8} alignItems='center'>
                                {article &&
                                    <InfoChip>
                                        <Flex flexDirection='row' gap={8} alignItems='center'>
                                            <ImportanceAvatar importance={article.importance} />
                                            <Label>Version {article.version}</Label>
                                            {article.release !== null
                                                ? <small>Udgivet d. {toDateTimeString(article.release.releasedDate)}</small>
                                                : <small>Ikke udgivet</small>
                                            }
                                        </Flex>
                                    </InfoChip>
                                }
                            </Flex>

                            

                            <MainRightTwoColumnLayout
                                left={
                                    <Flex flexDirection='column' gap={16}>

                                        <Card variant="lightGray">
                                            <Flex flexDirection='column' gap={16} alignItems='stretch'>
                                                <SelectField
                                                    width='100%s'
                                                    label='Valgt version'
                                                    options={articles.map((a) => ({
                                                            value: a.version.toString(),
                                                            text: `Version ${a.version.toString()} - 
                                                                    ${a.release  ? toDateTimeString(a.release.releasedDate) : 'afventer udvigelse'}`
                                                    }))}
                                                    selectedValue={articles[articleIndex].version.toString()}
                                                    onChange={(value) => {
                                                        const newIndex = articles.findIndex(a => a.version.toString() === value);
                                                        if (newIndex !== -1) {
                                                            setArticleIndex(newIndex);
                                                        }
                                                    }}
                                                />
                                                <Button variant="filled" size="32px" text="Vis lovkravet" onClick={() => setSelectedView('article')} />
                                            </Flex>
                                        </Card>

                                        {subscribers && 
                                            <Card variant="outlined">
                                                <>
                                                    <Flex flexDirection='column' alignItems='center' gap={8}>
                                                        <span>Lovkravet har <Em>{subscribers.length}</Em> abonnenter</span>
                                                        <Button variant="outlined" size="32px" text="Vis abonnenter" disabled={articles[articleIndex].isDeleted} onClick={() => setSelectedView('subscribers')} />
                                                    </Flex>

                                                </>
                                            </Card>
                                        }
                                        
                                        {article?.notes && 
                                            <Card variant="outlined">
                                                <>
                                                    <Flex flexDirection='column' alignItems='center' gap={8}>
                                                        <div>
                                                        {article.notes.length === 0
                                                            ? <span><Em>Ingen</Em> noter oprettet</span>
                                                            : <span>Lovkravet har <Em>{article.notes.length}</Em> noter</span>}
                                                        </div>

                                                        {article.notes.length > 0 &&
                                                            <div>
                                                                <span>Seneste note: <Em><i>{toDateTimeString(article.notes[article.notes.length - 1].created)}</i></Em></span>
                                                            </div>
                                                        }
                                                        <Button variant="outlined" size="32px" text="Vis noter" onClick={() => setSelectedView('notes')} />
                                                    </Flex>

                                                </>
                                            </Card>
                                        }

                                        {article && 
                                            <Card variant="outlined">
                                                <Flex flexDirection='column' alignItems='center' gap={8}>
                                                    <div>
                                                    {(article as Article).release ?
                                                        (article as Article).releaseRecords.length === 0
                                                            ? <span>Lovkravet har <Em>ingen</Em> udgivelser</span>
                                                            : <span>Lovkravet er udgivet til <Em>{(article as Article).releaseRecords.length.toString()}</Em> abonnenter</span>
                                                    : (article as Article).releaseRecords.length === 0
                                                            ? <span><Em>Ingen</Em> udgivelser vil finde sted</span>
                                                            : <span>Artiklen har <Em>{(article as Article).releaseRecords.length.toString()}</Em> afventende udgivelser</span>
                                                    }
                                                    </div>
                                                    <Button variant="outlined" size="32px" text="Vis udgivelser" onClick={() => setSelectedView('releases')} />
                                                </Flex>
                                            </Card>
                                        }
                                    </Flex>
                                }

                                main={
                                    <>

                                        {articles[0].isDeleted &&
                                            <MessageCard variant='error' style={{ marginBottom: '16px' }}>
                                                Dette lovkrav er slettet
                                            </MessageCard>
                                        }

                                        {selectedView === 'article' &&
                                            <ArticleVersionView
                                                article={articles[articleIndex]}
                                                oldVersion={articleIndex < articles.length - 1 ? articles[articleIndex + 1] : null}
                                                types={types}
                                                lists={lists}
                                                latestVersion={articleIndex === 0}
                                                onChanged={handleReload}
                                            />
                                        }

                                        {selectedView === 'subscribers' &&
                                            <ArticleViewSubscriberTable
                                                subscribers={subscribers}
                                                articleId={params.id || ''}
                                                isDeleted={articles[0].isDeleted}
                                                onSubscribersChanged={handleReload}
                                            />
                                        }

                                        {selectedView === 'notes' &&
                                            
                                            <ArticleViewNotesPanel articleVersionId={article?.articleVersionId || ''}
                                                activeUser={activeUser as AmorgAdminToken}
                                                notes={(article as Article).notes}
                                                onChanged={handleReload} 
                                            />
                                        }

                                        {selectedView === 'releases' &&
                                            <ArticleViewReleasePanel
                                                article={article as Article}
                                                contacts={articleContacts}
                                                releaseRecords={(article as Article).releaseRecords}
                                                onChanged={handleReload} />
                                        }
                                    </>
                                }
                            />

                        </>
                    }
                </Flex>
            </SingleColumnLayout>

        </AmorgyPageWrapper>
    );
}

function ArticleVersionView({article, oldVersion, types, lists, latestVersion, onChanged}:{article:Article, oldVersion:Article|null, types:ArticleType[], lists:ArticleList[], latestVersion:boolean, onChanged:()=>Promise<void>}):JSX.Element
{
    
    return (
        <Card variant='outlined'>

            <Flex flexDirection="column" gap={32}>
                <ArticleViewFields types={types} lists={lists}
                    oldVersion={oldVersion}
                    newVersion={article}
                    latestVersion={latestVersion}
                    onChanged={onChanged}
                />

                <ArticleViewAppendixPanel
                    articleVersionId={article.articleVersionId}
                    types={types}
                    lists={lists}
                    appendices={article.appendices}
                    canEdit={article.release === null}
                    onChanged={onChanged} 
                />
            </Flex>
        </Card>
    );
}

type ReleaseModalButtonProps = {
    articleVersion: number,
    articleDescription: string,
    articleAssessment: string,
    disabled?: boolean,
    onClickRelease: (silentRelease: boolean) => void;
};
function ReleaseModalButton (props: ReleaseModalButtonProps): JSX.Element
{
    const [showModal, setShowModal] = React.useState(false);
    const [sendNotifications, setSendNotifications] = React.useState(true);

    return (
        <>
            <Button
                variant="filled"
                onClick={() =>
                {
                    setShowModal(true);
                    setSendNotifications(true);
                }}
                text="Udgiv version"
                tooltipContent="Når du udgiver en ny version, vil alle aktuelle abonnenters kontaktpersoner blive informeret om ændringerne."
                disabled={props.disabled}
            />

            <ConfirmationModal
                showModal={showModal}
                buttonText="Udgiv"
                title="Udgiv nyeste version"
                content={
                    <Flex flexDirection='column' gap={16}>
                        <div>Er du sikker på at du vil udgive version {props.articleVersion} ?</div>
                        {(props.articleDescription.trim() === '' || props.articleAssessment.trim() === '') &&
                            <div style={{ color: colors.icon.error }}>
                                <span><b>*BEMÆRK!*</b></span><br />
                                <span>Ændringsbeskrivelse og/eller vurdering er ikke udfyldt!!</span>
                            </div>
                        }
                        <Flex>
                            <OnOff active={sendNotifications} onClick={(active) => setSendNotifications(active)} />
                            <span style={{ marginLeft: 8 }}>Send notifikationer</span>
                        </Flex>
                    </Flex>}
                onCancel={() => setShowModal(false)}
                onConfirm={() =>
                {
                    props.onClickRelease(!sendNotifications);
                    setShowModal(false);
                }}
                requireKey
            />
        </>
    );
}

type MergeModalButtonProps = {
    articleVersion: number,
    disabled?: boolean,
    onClickMerge: () => void;
};
function MergeModalButton (props: MergeModalButtonProps): JSX.Element
{
    const [showModal, setShowModal] = React.useState(false);

    return (
        <>
            <Button
                variant='outlined'
                onClick={() => setShowModal(true)}
                text='Flet med forrige version'
                tooltipContent="Når du fletter en ny version med den forrige, vil den nye versions værdier overskrive den forrige versions, men abonnenter vil ikke blive informeret om ændringerne og den nye version vil blive slettet. På denne måde kan der laves en ubemærket opdatering af et lovkrav."
                disabled={props.disabled}
            />

            <ConfirmationModal
                showModal={showModal}
                buttonText='Flet version'
                title='Overskriv forrige version'
                content={
                    <Flex flexDirection='column' gap={16}>
                        <div>Er du sikker på at du vil overskrive version {props.articleVersion - 1} med version {props.articleVersion}?</div>
                        <div>Version {props.articleVersion} slettes efter fletningen, og abonnenter vil ikke blive informeret om ændringerne.</div>
                    </Flex>
                }
                onCancel={() => setShowModal(false)}
                onConfirm={() =>
                {
                    props.onClickMerge();
                    setShowModal(false);
                }}
                requireKey
            />
        </>
    );
}
